import React from "react";
import { Filter, RotateCcw } from "lucide-react";
import { DocumentFiltersState } from "./documentTypes";

interface Props {
  filters: DocumentFiltersState;
  onChange: (updates: Partial<DocumentFiltersState>) => void;
  onReset: () => void;
}

const dateOptions = [
  { value: "", label: "Any date" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
];

const sortOptions = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "most_viewed", label: "Most viewed" },
];

export default function DocumentFilters({ filters, onChange, onReset }: Props) {
  const inputClass = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500";

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
          <Filter size={16} />
          Filters
        </div>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800"
        >
          <RotateCcw size={14} />
          Reset
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <input className={inputClass} placeholder="Category" value={filters.category} onChange={(e) => onChange({ category: e.target.value })} />
        <input className={inputClass} placeholder="Uploaded by (admin username)" value={filters.uploaded_by} onChange={(e) => onChange({ uploaded_by: e.target.value })} />
        <input className={inputClass} placeholder="File type (e.g. pdf, txt)" value={filters.file_type} onChange={(e) => onChange({ file_type: e.target.value })} />
        <input className={inputClass} placeholder="Tag" value={filters.tag} onChange={(e) => onChange({ tag: e.target.value })} />

        <select className={inputClass} value={filters.date_filter} onChange={(e) => onChange({ date_filter: e.target.value, date_from: "", date_to: "" })}>
          {dateOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400">From date</label>
          <input className={inputClass} type="date" value={filters.date_from} onChange={(e) => onChange({ date_from: e.target.value, date_filter: "" })} />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400">To date</label>
          <input className={inputClass} type="date" value={filters.date_to} onChange={(e) => onChange({ date_to: e.target.value, date_filter: "" })} />
        </div>

        <select className={inputClass} value={filters.sort_by} onChange={(e) => onChange({ sort_by: e.target.value })}>
          {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>

        <select className={inputClass} value={filters.is_pinned} onChange={(e) => onChange({ is_pinned: e.target.value })}>
          <option value="">Pinned: all</option>
          <option value="true">Pinned only</option>
          <option value="false">Unpinned only</option>
        </select>
        <select className={inputClass} value={filters.is_archived} onChange={(e) => onChange({ is_archived: e.target.value })}>
          <option value="false">Not archived</option>
          <option value="true">Archived</option>
          <option value="">All archive states</option>
        </select>
        <select className={inputClass} value={filters.is_active} onChange={(e) => onChange({ is_active: e.target.value })}>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
          <option value="">All statuses</option>
        </select>
      </div>
    </div>
  );
}
